import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { claimPendingSubscription } from '../../services/stripeService';
import './AuthPages.css';

const STRIPE_CHECKOUT_SESSION_KEY = 'gallop_stripe_checkout_session';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const passwordReset = (location.state as { passwordReset?: boolean } | null)?.passwordReset;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const { error } = await signIn(email, password);

    if (error) {
      setError(error.message);
      setLoading(false);
      return;
    }

    const checkoutId = sessionStorage.getItem(STRIPE_CHECKOUT_SESSION_KEY);
    const { data: sessionData } = await supabase.auth.getSession();
    const token = sessionData.session?.access_token;
    if (token) {
      try {
        const result = await claimPendingSubscription(token, checkoutId || undefined);
        if (!result.claimed && checkoutId) {
          console.warn('Login: checkout session id present but no pending subscription claimed');
        }
      } catch (claimErr) {
        console.warn('Login: claim pending subscription failed', claimErr);
      }
      sessionStorage.removeItem(STRIPE_CHECKOUT_SESSION_KEY);
    }

    navigate('/dashboard');
    setLoading(false);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <img src="/dark_favicon_logo.png" alt="Gallop AI" className="auth-logo" />
          <h1>Welcome Back</h1>
          <p>Sign in to your account to access today's races</p>
        </div>

        <form onSubmit={handleSubmit} className="auth-form">
          {passwordReset && (
            <div className="success-message">
              Your password has been updated. Sign in with your new password.
            </div>
          )}
          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="Enter your email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              placeholder="Enter your password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="auth-button primary"
          >
            {loading ? 'Signing In...' : 'Sign In'}
          </button>
        </form>

        <div className="auth-footer">
          <Link to="/forgot-password" className="auth-link">
            Forgot your password?
          </Link>
          <p className="auth-footer-hint">
            New to GallopAI?{' '}
            <Link to="/" className="auth-link">
              Start from the home page
            </Link>
          </p>
          <p className="auth-footer-hint">
            Paid at Stripe but need to finish your account?{' '}
            <Link to="/resume-signup" className="auth-link">
              Resume signup
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
